// @flow

import React from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {type DashboardLocationParamsType} from 'app/documents/types';
import {toggleProceduresDashboardRelation as toggleProceduresDashboardRelationAction} from 'app/procedures/actions';
import {type RouterType} from 'app/general/withTypedRouter';
import {
  CompactProcedureListItem,
  type MinimalRequiredProcedureFields,
} from 'app/procedures/components/CompactProcedureListItem';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {StyledListDiv} from 'app/general/bootstrap/utils';
import {ResetFilterMessage} from 'app/general/dashboards/ResetFilterMessage';

import {gt} from 'app/utils/i18n';
import type {StoredResponseType} from 'app/utils/redux_v2';
import type {contentType} from 'app/utils/content-type';
import {DocumentsNotificationsProvider} from 'app/notifly/components/DocumentsNotificationsProvider';
import {type StoreShapeType} from 'app/reducers/types';
import {PresenceSubscriber} from 'app/presence/components/PresenceSubscriber';
import {FolderLink} from 'app/folders/components/FolderLink';

type Props<T> = {|
  procedures: StoredResponseType<T>,
  resetFilters: () => void,
  refreshData: () => Promise<any>,
  viewingAll: boolean,
  ordering: string | void,
  canCreate: boolean,
  router: RouterType<any>,
  query: DashboardLocationParamsType,
  folder_hashid?: string,
|};

export function ProcedureList<T: MinimalRequiredProcedureFields>({
  procedures,
  canCreate,
  viewingAll,
  ordering,
  resetFilters,
  refreshData,
  query,
  folder_hashid,
}: Props<T>) {
  const activeRelations = useSelector<StoreShapeType, {[contentType]: boolean}>(
    // $FlowFixMe
    (state) =>
      state.proceduresPage.getIn(['dashboard', 'activeRelations']).toJS()
  );
  const dispatch = useDispatch();
  const toggleProceduresDashboardRelation = (relation: contentType) =>
    dispatch(toggleProceduresDashboardRelationAction(relation));
  return procedures.loading || procedures.results.length > 0 ? (
    <>
      {folder_hashid != null ? (
        <div className="my-2 text-center">
          <h4>
            <i className="far fa-folder-open" /> {gt('Folder contents')}
          </h4>
          <span>
            <button
              className="btn-outline-secondary btn btn-sm"
              onClick={resetFilters}
            >
              <i className="fa fa-times" />{' '}
              {gt('Reset filters and view all procedures.')}
            </button>
            <FolderLink hashid={folder_hashid} className="small ml-3" />
          </span>
        </div>
      ) : null}
      <DocumentsNotificationsProvider
        contentType="procedure"
        documentsIds={procedures.results.map((p) => p.id)}
      >
        {(proceduresNotifications) => (
          <StyledListDiv>
            {procedures.results.map((item) => (
              <CompactProcedureListItem
                key={item.url}
                object={item}
                refreshData={refreshData}
                extra={{
                  showRelations: true,
                  ordering,
                  activeRelations,
                  toggleProceduresDashboardRelation,
                  proceduresNotifications,
                }}
              />
            ))}
          </StyledListDiv>
        )}
      </DocumentsNotificationsProvider>
      <PresenceSubscriber
        contentType="procedure"
        objectsIds={procedures.results.map((p) => p.id)}
        stealth={true}
      />
    </>
  ) : (
    <NoResultsMessage
      className="js-procedures-list js-edit-dropdown"
      head={
        viewingAll ? (
          gt('No procedures on your account')
        ) : folder_hashid != null ? (
          <h4>
            <i className="far fa-folder-open" /> {gt('Folder contents')}
          </h4>
        ) : (
          gt('No procedures found with these filters')
        )
      }
    >
      {(FullHTMLLink, urls) =>
        viewingAll ? (
          canCreate ? (
            <FullHTMLLink
              className="btn btn-primary btn-lg"
              html_url={urls.PROCEDURES_ADD_NEW}
            >
              {gt('Create a Procedure')}
            </FullHTMLLink>
          ) : null
        ) : (
          <span>
            <ResetFilterMessage
              query={query}
              resetFilters={resetFilters}
              ct="procedure"
            />

            {folder_hashid != null ? (
              <FolderLink hashid={folder_hashid} className="small ml-3" />
            ) : null}
          </span>
        )
      }
    </NoResultsMessage>
  );
}
